import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import {
  ArrowSquareOut,
  FileDashed,
  ListBullets,
  Archive,
  Eye,
} from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import {
  ContactType,
  ContractContact,
  ContractMilestoneDate,
  ContractPhase,
  MilestoneDateType,
  RentalContract,
} from "shared/types/rental-contracts";
import { formatDate } from "shared/utils/get-dates";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Skeleton } from "components/ui/skeleton";
import { Separator } from "components/ui/separator";
import ModalViewContract from "screens/buildings/components/rentalTab/modalViewContract";

interface UnitRentalTabProps {
  unitId: string;
  buildingId: string;
}

function getMilestone(
  contract: RentalContract,
  type: MilestoneDateType,
): Date | null {
  const milestone = contract.contractMilestoneDates?.find(
    (m: ContractMilestoneDate) => m.milestoneDateType === type,
  );
  return milestone?.date ? new Date(milestone.date) : null;
}

function getTenantNames(contract: RentalContract): string {
  const tenants =
    contract.contractContacts?.filter(
      (c: ContractContact) => c.contactType === ContactType.TENANT,
    ) ?? [];
  const names = tenants
    .map((t) => t.resource?.name)
    .filter((n): n is string => Boolean(n));
  return names.length > 0 ? names.join(", ") : "—";
}

function formatEuro(price?: number | null): string {
  if (price == null) return "—";
  return `€ ${price.toFixed(2)}`;
}

function phaseBadgeVariant(
  phase: ContractPhase,
): "default" | "secondary" | "outline" | "destructive" {
  switch (phase) {
    case ContractPhase.ACTIVE:
      return "default";
    case ContractPhase.TERMINATION_PHASE:
      return "destructive";
    case ContractPhase.CONCEPT:
      return "secondary";
    case ContractPhase.CLOSED:
    default:
      return "outline";
  }
}

function ContractRow({
  contract,
  onOpen,
}: {
  contract: RentalContract;
  onOpen: (contract: RentalContract) => void;
}) {
  const startDate = getMilestone(contract, MilestoneDateType.START_DATE);
  const endDate = getMilestone(contract, MilestoneDateType.END_DATE);
  const cancellationDate = getMilestone(
    contract,
    MilestoneDateType.CANCELLATION_DATE,
  );

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(contract)}
        className="flex w-full flex-col gap-2 py-3 text-left hover:bg-neutral-primary/5 focus:bg-neutral-primary/5 focus:outline-none sm:flex-row sm:items-center sm:justify-between"
        aria-label={`Open contract ${contract.id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-neutral-primary">
              Contract #{contract.id}
            </span>
            <Badge variant={phaseBadgeVariant(contract.phase)}>
              {contract.phase}
            </Badge>
            {contract.legalForm && (
              <span className="text-xs text-neutral-secondary-mid">
                {contract.legalForm}
              </span>
            )}
          </div>
          <p className="mt-1 truncate text-sm text-neutral-secondary-mid">
            Huurder: {getTenantNames(contract)}
          </p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-secondary-mid">
            {startDate && <span>Start: {formatDate(startDate)}</span>}
            {endDate && <span>Einde: {formatDate(endDate)}</span>}
            {cancellationDate && (
              <span>Opzeg: {formatDate(cancellationDate)}</span>
            )}
            {contract.activePrice?.price != null && (
              <span>Huur: {formatEuro(contract.activePrice.price)}</span>
            )}
          </div>
        </div>
        <Eye
          className="hidden h-5 w-5 shrink-0 text-moss-primary sm:block"
          weight="duotone"
          aria-hidden="true"
        />
      </button>
    </li>
  );
}

function ContractSection({
  title,
  icon,
  contracts,
  emptyText,
  onOpenContract,
}: {
  title: string;
  icon: React.ReactNode;
  contracts: RentalContract[];
  emptyText: string;
  onOpenContract: (contract: RentalContract) => void;
}) {
  return (
    <section>
      <div className="flex items-center gap-2 text-sm font-semibold text-neutral-primary">
        {icon}
        <span>{title}</span>
        <span className="text-xs font-normal text-neutral-secondary-mid">
          ({contracts.length})
        </span>
      </div>
      {contracts.length === 0 ? (
        <p className="mt-2 rounded-md border border-dashed border-neutral-primary/20 p-4 text-center text-sm text-neutral-secondary-mid">
          {emptyText}
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-neutral-primary/10">
          {contracts.map((c) => (
            <ContractRow key={c.id} contract={c} onOpen={onOpenContract} />
          ))}
        </ul>
      )}
    </section>
  );
}

export const UnitRentalTab = ({ unitId, buildingId }: UnitRentalTabProps) => {
  const { session } = useSession();
  const [contracts, setContracts] = useState<RentalContract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedContract, setSelectedContract] =
    useState<RentalContract | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const { conceptContracts, activeContracts, terminatingContracts, closedContracts } =
    useMemo(() => {
      const concept = contracts.filter(
        (c) => c.phase === ContractPhase.CONCEPT,
      );
      const active = contracts.filter(
        (c) => c.phase === ContractPhase.ACTIVE,
      );
      const terminating = contracts.filter(
        (c) => c.phase === ContractPhase.TERMINATION_PHASE,
      );
      const closed = contracts.filter(
        (c) => c.phase === ContractPhase.CLOSED,
      );
      return {
        conceptContracts: concept,
        activeContracts: active,
        terminatingContracts: terminating,
        closedContracts: closed,
      };
    }, [contracts]);

  const fetchContracts = useCallback(async () => {
    if (!unitId) return;
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/unit/${unitId}`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error(`Contracten ophalen mislukt (${response.status})`);
      }
      const data: RentalContract[] = await response.json();
      setContracts(Array.isArray(data) ? data : []);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "unit-detail", tab: "rental" },
        extra: { unitId },
      });
      toast.error("Contracten konden niet worden geladen.");
      console.error("Failed to fetch unit contracts", error);
    } finally {
      setIsLoading(false);
    }
  }, [unitId, session]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleOpenContract = useCallback((contract: RentalContract) => {
    setSelectedContract(contract);
    setIsViewModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsViewModalOpen(false);
    setSelectedContract(null);
  }, []);

  const totalCount = contracts.length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ListBullets className="h-5 w-5" weight="duotone" />
            Verhuurcontracten
          </CardTitle>
          <p className="mt-1 text-sm text-neutral-secondary-mid">
            {isLoading
              ? "Laden..."
              : totalCount === 0
                ? "Geen contracten gekoppeld aan deze unit"
                : `${totalCount} contract${totalCount === 1 ? "" : "en"} op deze unit`}
          </p>
        </div>
        {buildingId && (
          <Button asChild variant="outline" size="sm">
            <Link to={`/building/${buildingId}/verhuur`}>
              Beheren in Verhuur-tab
              <ArrowSquareOut className="ml-2 h-4 w-4" weight="bold" />
            </Link>
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : (
          <>
            <ContractSection
              title="Contracten in ontwerp"
              icon={<FileDashed className="h-4 w-4" weight="duotone" />}
              contracts={conceptContracts}
              emptyText="Geen contracten in ontwerp."
              onOpenContract={handleOpenContract}
            />
            <Separator />
            <ContractSection
              title="Lopende contracten"
              icon={<ListBullets className="h-4 w-4" weight="duotone" />}
              contracts={activeContracts}
              emptyText="Geen lopende contracten."
              onOpenContract={handleOpenContract}
            />
            <Separator />
            <ContractSection
              title="Contracten in opzeg"
              icon={<ListBullets className="h-4 w-4" weight="duotone" />}
              contracts={terminatingContracts}
              emptyText="Geen contracten in opzeg."
              onOpenContract={handleOpenContract}
            />
            {closedContracts.length > 0 && (
              <>
                <Separator />
                <ContractSection
                  title="Gearchiveerde contracten"
                  icon={<Archive className="h-4 w-4" weight="duotone" />}
                  contracts={closedContracts}
                  emptyText="Geen gearchiveerde contracten."
                  onOpenContract={handleOpenContract}
                />
              </>
            )}
          </>
        )}
      </CardContent>

      {selectedContract && (
        <ModalViewContract
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
          incomingContract={selectedContract}
          fetchRentalContracts={fetchContracts}
        />
      )}
    </Card>
  );
};
